/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { initializeApp } from 'firebase/app';
import {
  createUserWithEmailAndPassword,
  getAuth,
  GithubAuthProvider,
  GoogleAuthProvider,
  onIdTokenChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  TwitterAuthProvider,
  User,
} from 'firebase/auth';
// config
import { FIREBASE_API } from 'src/config-global';
//
import { AuthContext } from './auth-context';
import { ActionMapType, AuthStateType, AuthUserType } from '../../types';
import { getUserMe, postSignIn } from '../../../services/users';

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const firebaseApp = initializeApp(FIREBASE_API);
const AUTH = getAuth(firebaseApp);

// ----------------------------------------------------------------------

enum Types {
  INITIAL = 'INITIAL',
}

type Payload = {
  [Types.INITIAL]: {
    user: AuthUserType;
  };
};

type Action = ActionMapType<Payload>[keyof ActionMapType<Payload>];

const initialState: AuthStateType = {
  user: null,
  loading: true,
  dataUser: {},
  permissions: [],
};

const reducer = (state: AuthStateType, action: Action) => {
  if (action.type === Types.INITIAL) {
    return {
      loading: false,
      user: action.payload.user,
      dataUser: action.payload.user?.dataUser,
      permissions: action.payload.user?.permissions,
    };
  }
  return state;
};

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // Guarda el token solo si cambia
  const saveToken = async (user: User) => {
    const token = await user.getIdToken();
    if (localStorage.getItem('key') !== token) {
      localStorage.setItem('key', token);
    }
    return token;
  };

  // Limpia el token del localStorage y el estado
  const clearToken = () => {
    localStorage.removeItem('key');
    dispatch({
      type: Types.INITIAL,
      payload: {
        user: null,
      },
    });
  };

  const saveEmpty = () => {
    clearToken();
  };

  const magicLogin = useCallback(async (user: User, token: string) => {
    const signIn = await postSignIn(token);
    if (signIn) {
      const response = await getUserMe();
      if (!response) {
        saveEmpty();
      } else {
        dispatch({
          type: Types.INITIAL,
          payload: {
            user: {
              ...user,
              id: user.uid,
              role: response.auth?.role,
              dataUser: response.auth?.user,
              permissions: response.auth?.permissions,
            },
          },
        });
      }
    } else {
      saveEmpty();
    }
  }, []);

  useEffect(() => {
    const unsubscribe = onIdTokenChanged(AUTH, async (user) => {
      try {
        if (user) {
          const token = await saveToken(user);
          await magicLogin(user, token);
        } else {
          clearToken();
        }
      } catch (error) {
        console.error(error);
        clearToken();
      }
    });
    return () => {
      unsubscribe();
    };
  }, [magicLogin]);

  // LOGIN
  const login = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(AUTH, email, password);
  }, []);

  const loginWithGoogle = useCallback(async () => {
    const provider = new GoogleAuthProvider();

    await signInWithPopup(AUTH, provider);
  }, []);

  const loginWithGithub = useCallback(async () => {
    const provider = new GithubAuthProvider();

    await signInWithPopup(AUTH, provider);
  }, []);

  const loginWithTwitter = useCallback(async () => {
    const provider = new TwitterAuthProvider();

    await signInWithPopup(AUTH, provider);
  }, []);

  // REGISTER
  const register = useCallback(
    async (email: string, password: string, firstName: string, lastName: string) => {
      const newUser = await createUserWithEmailAndPassword(AUTH, email, password);

      await sendEmailVerification(newUser.user);
    },
    []
  );

  // LOGOUT
  const logout = useCallback(async () => {
    await signOut(AUTH);
    clearToken();
  }, []);

  // FORGOT PASSWORD
  const forgotPassword = useCallback(async (email: string) => {
    await sendPasswordResetEmail(AUTH, email);
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';
  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      dataUser: state.user?.dataUser,
      role: state.user?.role,
      permissions: state.user?.permissions,
      method: 'firebase',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      logout,
      register,
      forgotPassword,
      loginWithGoogle,
      loginWithGithub,
      loginWithTwitter,
    }),
    [
      status,
      state.user,
      login,
      logout,
      register,
      forgotPassword,
      loginWithGithub,
      loginWithGoogle,
      loginWithTwitter,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
